import React, { useState, useEffect } from 'react';
import {
    BarChart3,
    Calendar,
    Users,
    Clock,
    AlertCircle,
    CheckCircle2,
    ArrowRight,
    TrendingUp,
    Filter,
    Briefcase,
    ChevronDown,
    ChevronUp,
    Calculator
} from 'lucide-react';
import api from '../services/api';
import { format, addMonths, subMonths } from 'date-fns';
import ptBR from 'date-fns/locale/pt-BR';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

// Cores por cargo (mesmo do sistema de horas de projeto)
const ROLE_COLORS = {
    GERENCIA: { bg: 'bg-purple-100', text: 'text-purple-700', border: 'border-purple-300' },
    RECEPCAO_COMPRAS_AGENDA: { bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-300' },
    RECEPCAO_PROJETOS: { bg: 'bg-indigo-100', text: 'text-indigo-700', border: 'border-indigo-300' },
    PRODUCAO: { bg: 'bg-green-100', text: 'text-green-700', border: 'border-green-300' },
    MONTAGEM_CLIENTE: { bg: 'bg-orange-100', text: 'text-orange-700', border: 'border-orange-300' }
};

const ROLE_LABELS = {
    GERENCIA: 'Gerência',
    RECEPCAO_COMPRAS_AGENDA: 'Compras/Agenda',
    RECEPCAO_PROJETOS: 'Projetos',
    PRODUCAO: 'Produção',
    MONTAGEM_CLIENTE: 'Montagem'
};

const CapacityCounter = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [currentMonth, setCurrentMonth] = useState(new Date());
    const [summary, setSummary] = useState(null);
    const [breakdownData, setBreakdownData] = useState({ employees: [], roles: [] });
    const [committedHoursByRole, setCommittedHoursByRole] = useState([]);
    const [viewType, setViewType] = useState('employees'); // 'employees' ou 'roles'
    const [expandedProjects, setExpandedProjects] = useState({});

    useEffect(() => {
        fetchData();
    }, [currentMonth]);

    const fetchData = async () => {
        try {
            setLoading(true);
            const monthStr = format(currentMonth, 'yyyy-MM');

            const [summaryRes, breakdownRes, committedRes] = await Promise.all([
                api.get(`/capacity/summary?month=${monthStr}`),
                api.get(`/capacity/breakdown?month=${monthStr}`),
                api.get(`/capacity/committed-by-role?month=${monthStr}`)
            ]);

            setSummary(summaryRes.data.data);
            setBreakdownData(breakdownRes.data.data);
            setCommittedHoursByRole(committedRes.data.data || []);
        } catch (error) {
            console.error('Erro ao buscar dados de capacidade:', error);
            toast.error('Erro ao carregar dados de capacidade');
        } finally {
            setLoading(false);
        }
    };

    const nextMonth = () => setCurrentMonth(prev => addMonths(prev, 1));
    const prevMonth = () => setCurrentMonth(prev => subMonths(prev, 1));

    const toggleProjectExpand = (projectId) => {
        setExpandedProjects(prev => ({
            ...prev,
            [projectId]: !prev[projectId]
        }));
    };

    // Agrupar horas comprometidas por projeto
    const projectsGrouped = committedHoursByRole.reduce((acc, item) => {
        const key = item.project_id;
        if (!acc[key]) {
            acc[key] = {
                project_id: item.project_id,
                project_name: item.project_name,
                project_code: item.project_code,
                client_name: item.client_name,
                total_hours: 0,
                roles: []
            };
        }
        acc[key].total_hours += parseFloat(item.estimated_hours || 0);
        acc[key].roles.push({
            role: item.role,
            hours: item.estimated_hours,
            hours_per_day: item.scheduled_hours_per_day,
            assigned_to_name: item.assigned_to_name
        });
        return acc;
    }, {});

    const projectsList = Object.values(projectsGrouped);

    if (loading && !summary) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
            </div>
        );
    }

    return (
        <div className="p-6 space-y-6 max-w-7xl mx-auto">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-secondary-900 flex items-center gap-2">
                        <BarChart3 className="w-7 h-7 text-primary-600" />
                        Contador de Capacidade
                    </h1>
                    <p className="text-secondary-500 text-sm">
                        Gestão de carga horária e disponibilidade produtiva
                    </p>
                </div>

                <div className="flex items-center gap-3">
                    <button
                        onClick={() => navigate('/capacidade/simulador')}
                        className="flex items-center gap-2 bg-[#3a7630] text-white px-4 py-2 rounded-lg hover:bg-[#2d5a25] transition-colors"
                    >
                        <Calculator className="w-4 h-4" />
                        Simular Prazo
                    </button>

                    <div className="flex items-center gap-3 bg-white p-2 rounded-lg border border-secondary-200 shadow-sm">
                        <button
                            onClick={prevMonth}
                            className="p-2 hover:bg-secondary-50 rounded-md transition-colors"
                        >
                            <ArrowRight className="w-4 h-4 rotate-180" />
                        </button>
                        <div className="flex items-center gap-2 px-4 font-bold text-secondary-900 capitalize">
                            <Calendar className="w-4 h-4 text-primary-500" />
                            {format(currentMonth, 'MMMM yyyy', { locale: ptBR })}
                        </div>
                        <button
                            onClick={nextMonth}
                            className="p-2 hover:bg-secondary-50 rounded-md transition-colors"
                        >
                            <ArrowRight className="w-4 h-4" />
                        </button>
                    </div>
                </div>
            </div>

            {/* Metrics Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <MetricCard
                    title="Capacidade Bruta"
                    value={`${summary?.total_capacity_hours}h`}
                    subtitle="Total de jornadas ativas"
                    icon={<Clock className="w-5 h-5" />}
                    color="blue"
                />
                <MetricCard
                    title="Deduções"
                    value={`-${summary?.deductions_hours}h`}
                    subtitle="Feriados e ausências"
                    icon={<AlertCircle className="w-5 h-5" />}
                    color="amber"
                />
                <MetricCard
                    title="Capacidade Líquida"
                    value={`${summary?.net_capacity_hours}h`}
                    subtitle="Horas reais disponíveis"
                    icon={<TrendingUp className="w-5 h-5" />}
                    color="emerald"
                />
                <MetricCard
                    title="Comprometido"
                    value={`${summary?.committed_hours}h`}
                    subtitle="Projetos em produção"
                    icon={<Filter className="w-5 h-5" />}
                    color="indigo"
                />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Main Utilization Chart/Visual */}
                <div className="lg:col-span-2 bg-white p-6 rounded-xl border border-secondary-200 shadow-sm space-y-6">
                    <div className="flex items-center justify-between">
                        <h3 className="font-bold text-secondary-900">Utilização Mensal</h3>
                        <span className={`px-3 py-1 rounded-full text-xs font-bold ${summary?.usage_percentage > 90 ? 'bg-red-100 text-red-700' :
                            summary?.usage_percentage > 70 ? 'bg-amber-100 text-amber-700' :
                                'bg-green-100 text-green-700'
                            }`}>
                            {summary?.usage_percentage}% de ocupação
                        </span>
                    </div>

                    <div className="relative h-8 bg-secondary-100 rounded-full overflow-hidden border border-secondary-200">
                        <div
                            className={`absolute top-0 left-0 h-full transition-all duration-1000 ease-out ${summary?.usage_percentage > 90 ? 'bg-red-500' :
                                summary?.usage_percentage > 70 ? 'bg-amber-500' :
                                    'bg-primary-500'
                                }`}
                            style={{ width: `${Math.min(summary?.usage_percentage, 100)}%` }}
                        />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div className="p-4 bg-secondary-50 rounded-lg">
                            <span className="text-secondary-500 text-xs block mb-1">Saldo Disponível</span>
                            <span className={`text-xl font-bold ${summary?.balance_hours < 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                                {summary?.balance_hours}h
                            </span>
                        </div>
                        <div className="p-4 bg-secondary-50 rounded-lg">
                            <span className="text-secondary-500 text-xs block mb-1">Status de Carga</span>
                            <span className="text-sm font-bold text-secondary-900">
                                {summary?.usage_percentage > 90 ? 'Carga Crítica' :
                                    summary?.usage_percentage > 70 ? 'Carga Otimizada' :
                                        'Capacidade Ociosa'}
                            </span>
                        </div>
                    </div>
                </div>

                {/* Breakdown List */}
                <div className="bg-white p-6 rounded-xl border border-secondary-200 shadow-sm">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="font-bold text-secondary-900 flex items-center gap-2">
                            <Users className="w-5 h-5 text-secondary-400" />
                            Detalhamento
                        </h3>
                        <div className="flex bg-secondary-100 p-1 rounded-lg">
                            <button
                                onClick={() => setViewType('employees')}
                                className={`px-3 py-1 text-[10px] font-bold rounded-md transition-all ${viewType === 'employees' ? 'bg-white text-primary-600 shadow-sm' : 'text-secondary-500 hover:text-secondary-700'}`}
                            >
                                Funcionários
                            </button>
                            <button
                                onClick={() => setViewType('roles')}
                                className={`px-3 py-1 text-[10px] font-bold rounded-md transition-all ${viewType === 'roles' ? 'bg-white text-primary-600 shadow-sm' : 'text-secondary-500 hover:text-secondary-700'}`}
                            >
                                Cargos
                            </button>
                        </div>
                    </div>

                    <div className="space-y-4 max-h-[400px] overflow-y-auto pr-2 custom-scrollbar">
                        {viewType === 'employees' ? (
                            breakdownData.employees.map(emp => (
                                <div key={emp.id} className="p-3 border border-secondary-100 rounded-lg hover:bg-secondary-50 transition-colors">
                                    <div className="flex justify-between items-start mb-2">
                                        <div>
                                            <span className="text-sm font-bold text-secondary-900 block">{emp.name}</span>
                                            <span className="text-[10px] text-secondary-400 uppercase tracking-tighter">{emp.role}</span>
                                        </div>
                                        <div className="text-right">
                                            <span className="text-xs font-bold text-primary-600 block">{emp.net_hours}h líq.</span>
                                            <span className="text-[10px] text-secondary-400">Total: {emp.gross_hours}h</span>
                                        </div>
                                    </div>
                                    <div className="w-full bg-secondary-100 h-1.5 rounded-full">
                                        <div
                                            className="bg-primary-500 h-full rounded-full"
                                            style={{ width: `${Math.min((emp.net_hours / (summary?.net_capacity_hours / (breakdownData.employees.length || 1))) * 100, 100)}%` }}
                                        />
                                    </div>
                                </div>
                            ))
                        ) : (
                            breakdownData.roles.map(role => (
                                <div key={role.role} className="p-3 border border-secondary-100 rounded-lg hover:bg-secondary-50 transition-colors">
                                    <div className="flex justify-between items-start mb-2">
                                        <div>
                                            <span className="text-sm font-bold text-secondary-900 block">{role.role}</span>
                                            <span className="text-[10px] text-secondary-400 uppercase tracking-tighter">{role.employee_count} funcionário(s)</span>
                                        </div>
                                        <div className="text-right">
                                            <span className="text-xs font-bold text-primary-600 block">{role.net_hours}h líq.</span>
                                            <span className="text-[10px] text-secondary-400">Total: {role.gross_hours}h</span>
                                        </div>
                                    </div>
                                    <div className="w-full bg-secondary-100 h-1.5 rounded-full">
                                        <div
                                            className="bg-primary-500 h-full rounded-full"
                                            style={{ width: `${Math.min((role.net_hours / (summary?.net_capacity_hours || 1)) * 100, 100)}%` }}
                                        />
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            </div>

            {/* Projetos Comprometidos */}
            {projectsList.length > 0 && (
                <div className="bg-white rounded-xl border border-secondary-200 shadow-sm">
                    <div className="p-4 border-b border-secondary-200">
                        <h3 className="font-bold text-secondary-900 flex items-center gap-2">
                            <Briefcase className="w-5 h-5 text-[#3a7630]" />
                            Projetos com Horas Comprometidas
                        </h3>
                        <p className="text-sm text-secondary-500 mt-1">
                            Lista de projetos que estão consumindo a capacidade deste mês
                        </p>
                    </div>

                    <div className="divide-y divide-secondary-100">
                        {projectsList.map(project => {
                            const isExpanded = expandedProjects[project.project_id];

                            return (
                                <div key={project.project_id}>
                                    <button
                                        onClick={() => toggleProjectExpand(project.project_id)}
                                        className="w-full p-4 flex items-center justify-between hover:bg-secondary-50 transition-colors"
                                    >
                                        <div className="flex items-center gap-3">
                                            {isExpanded ? (
                                                <ChevronUp className="w-5 h-5 text-secondary-400" />
                                            ) : (
                                                <ChevronDown className="w-5 h-5 text-secondary-400" />
                                            )}
                                            <div className="text-left">
                                                <div className="font-medium text-secondary-900">
                                                    {project.project_code} - {project.project_name}
                                                </div>
                                                <div className="text-sm text-secondary-500">
                                                    {project.client_name}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-4">
                                            <div className="text-right">
                                                <div className="font-bold text-[#3a7630]">
                                                    {project.total_hours.toFixed(1)}h
                                                </div>
                                                <div className="text-xs text-secondary-500">
                                                    {project.roles.length} cargo(s)
                                                </div>
                                            </div>
                                        </div>
                                    </button>

                                    {isExpanded && (
                                        <div className="px-4 pb-4 bg-secondary-50">
                                            <div className="ml-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                                                {project.roles.map((role, idx) => {
                                                    const colors = ROLE_COLORS[role.role] || ROLE_COLORS.PRODUCAO;
                                                    return (
                                                        <div
                                                            key={idx}
                                                            className={`p-3 rounded-lg border ${colors.bg} ${colors.border}`}
                                                        >
                                                            <div className="flex items-center justify-between mb-1">
                                                                <span className={`text-xs font-semibold ${colors.text}`}>
                                                                    {ROLE_LABELS[role.role] || role.role}
                                                                </span>
                                                            </div>
                                                            <div className="text-sm text-secondary-700">
                                                                <span className="font-semibold">{parseFloat(role.hours).toFixed(1)}h</span>
                                                                {role.hours_per_day && (
                                                                    <span className="text-xs text-secondary-500 ml-1">
                                                                        ({role.hours_per_day}h/dia)
                                                                    </span>
                                                                )}
                                                            </div>
                                                            {role.assigned_to_name && (
                                                                <div className="text-xs text-secondary-500 mt-1">
                                                                    Responsável: {role.assigned_to_name}
                                                                </div>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Information Alert */}
            <div className="bg-primary-50 border border-primary-100 p-4 rounded-xl flex gap-3">
                <CheckCircle2 className="w-5 h-5 text-primary-600 mt-0.5" />
                <div className="text-sm text-primary-800">
                    <strong>Como funciona o cálculo?</strong> A capacidade é baseada nas escalas ativas de cada funcionário.
                    Deduções automáticas são aplicadas para feriados e faltas registradas no sistema.
                    O comprometimento vem das horas estimadas em projetos com status em produção.
                </div>
            </div>
        </div>
    );
};

const MetricCard = ({ title, value, subtitle, icon, color }) => {
    const colors = {
        blue: 'bg-blue-50 text-blue-600 border-blue-100',
        amber: 'bg-amber-50 text-amber-600 border-amber-100',
        emerald: 'bg-emerald-50 text-emerald-600 border-emerald-100',
        indigo: 'bg-indigo-50 text-indigo-600 border-indigo-100'
    };

    return (
        <div className="bg-white p-5 rounded-xl border border-secondary-200 shadow-sm space-y-2">
            <div className="flex items-center justify-between">
                <span className="text-secondary-500 text-xs font-medium uppercase tracking-wider">{title}</span>
                <div className={`p-2 rounded-lg border ${colors[color]}`}>
                    {icon}
                </div>
            </div>
            <div className="flex flex-col">
                <span className="text-2xl font-bold text-secondary-900">{value}</span>
                <span className="text-[10px] text-secondary-400">{subtitle}</span>
            </div>
        </div>
    );
};

export default CapacityCounter;
